import { useEffect, useRef } from "react";
import { supabase, isSupabaseConfigured } from "../lib/supabase";
import { useAppContext } from "../store";
import { setSyncUser, getSyncUser, initialSync, pushProfile } from "../lib/sync/engine";
import { pullWordOfDayOptIn } from "../lib/sync/profile";

/**
 * Bridges Supabase Auth session state into the app: tracks cloudLinked,
 * registers the current user with the sync engine, and kicks off the
 * initial pull/push once signed in. Renders nothing.
 */
export function AuthBridge() {
  if (!isSupabaseConfigured) return null;
  return <AuthBridgeInner />;
}

function AuthBridgeInner() {
  const { state, updateSettings } = useAppContext();
  // Read via ref inside the auth listener so the subscription effect below
  // doesn't need to re-run (and re-subscribe) on every state change.
  const stateRef = useRef(state);
  useEffect(() => {
    stateRef.current = state;
  }, [state]);

  useEffect(() => {
    if (!supabase) return;

    const handleUserId = (userId: string | null) => {
      const current = stateRef.current;
      if (!userId) {
        if (current.settings.cloudLinked) updateSettings({ cloudLinked: false });
        setSyncUser(null);
        return;
      }
      if (!current.settings.cloudLinked) updateSettings({ cloudLinked: true });
      setSyncUser(userId);
      // Fire-and-forget — never blocks UI
      initialSync(userId)
        .then(() => {
          if (getSyncUser() !== userId) return;
          return pushProfile(userId, current.settings.role, current.profile.name || undefined).catch(
            (err) => console.warn("[sync] pushProfile after initialSync failed:", err),
          );
        })
        .catch((err) => console.warn("[sync] initialSync failed:", err));
      // So the Settings toggle reflects the account's actual state after
      // signing in on a different device, not just this device's default.
      pullWordOfDayOptIn(userId)
        .then((optIn) => {
          if (getSyncUser() !== userId) return;
          updateSettings({ wordOfDayOptIn: optIn });
        })
        .catch((err) => console.warn("[sync] pullWordOfDayOptIn failed:", err));
    };

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      handleUserId(session?.user.id ?? null);
    });

    return () => subscription.unsubscribe();
  }, [updateSettings]);

  // Push profile when role changes while the user is signed in.
  useEffect(() => {
    const current = stateRef.current;
    if (!current.settings.cloudLinked) return;
    const userId = getSyncUser();
    if (!userId) return;
    pushProfile(userId, current.settings.role, current.profile.name || undefined).catch((err) =>
      console.warn("[sync] pushProfile on role change failed:", err),
    );
    // Intentionally trigger only on role changes. The latest cloud/name values
    // come from stateRef, so this does not resubscribe or duplicate profile
    // pushes when the display name changes.
  }, [state.settings.role]);

  return null;
}
